import { secureStorage } from "@/utils/aes";
import { Buffer } from "buffer";
import { create } from "zustand";
import { usePubkeyStore } from "./pubkey";
interface AESGCM256_X25519_MIXED_Crypt {
  localPublicKey: CryptoKey | undefined;
  localPublicKeyStr: string;
  localPrivateKey: CryptoKey | undefined;
  remotePublicKey: string;
  getExchangeSecret: () => Promise<ArrayBuffer | null>;
  aes_gcm_decrypt(word: ArrayBuffer): Promise<ArrayBuffer>;
  decrypt(cipher: string): Promise<string>;
  aes_gcm_encrypt(plain: ArrayBuffer): Promise<Uint8Array>;
  encrypt(word: string): Promise<string>;
}
export const useCryptStore = create<AESGCM256_X25519_MIXED_Crypt>(
  (set, get) => {
    const localKeyPairPromise = window.crypto.subtle
      .generateKey(
        {
          name: "X25519",
          namedCurve: "X25519",
        },
        true,
        ["deriveKey", "deriveBits"],
      )
      .then(async (keyPair) => {
        const [publicKey, publicKeyStr, privateKey] = await Promise.all([
          keyPair.publicKey,
          window.crypto.subtle.exportKey("raw", keyPair.publicKey),
          keyPair.privateKey,
        ]);
        const encryptedPublicKey = await secureStorage.encrypt(
          Buffer.from(publicKeyStr).toString("base64"),
        );
        localStorage.setItem("local_public_key", encryptedPublicKey);
        return {
          localPublicKey: publicKey,
          localPublicKeyStr: Buffer.from(publicKeyStr).toString("base64"),
          localPrivateKey: privateKey,
        };
      });
    let localPublicKey: CryptoKey | undefined = undefined;
    let localPublicKeyStr = "";
    let localPrivateKey: CryptoKey | undefined = undefined;
    localKeyPairPromise.then(
      ({
        localPublicKey: pub,
        localPrivateKey: priv,
        localPublicKeyStr: pubstr,
      }) => {
        localPublicKey = pub;
        localPrivateKey = priv;
        localPublicKeyStr = pubstr;
      },
    );

    return {
      localPublicKey,
      localPublicKeyStr,
      localPrivateKey,
      remotePublicKey: "",
      getExchangeSecret: async () => {
        if (!localPrivateKey || !localPublicKey) {
          await localKeyPairPromise;
        }
        console.debug("Local Public Key:", localPublicKeyStr);
        if (!localPrivateKey || !localPublicKey) {
          return null;
        }
        if (!get().remotePublicKey) {
          const pubkeyStore = usePubkeyStore.getState();
          const remotePubkey = await pubkeyStore.getPubkey();
          set({ remotePublicKey: remotePubkey });
        }
        console.debug("Remote Public Key:", get().remotePublicKey);
        if (!get().remotePublicKey) {
          return null;
        }
        try {
          const remotePublicKeyBuffer = Buffer.from(
            get().remotePublicKey,
            "base64",
          );
          const importedRemotePublicKey = await window.crypto.subtle.importKey(
            "raw",
            remotePublicKeyBuffer,
            {
              name: "X25519",
              namedCurve: "X25519",
            },
            false,
            [],
          );
          const sharedSecret = await window.crypto.subtle.deriveBits(
            {
              name: "X25519",
              public: importedRemotePublicKey,
            },
            localPrivateKey,
            256,
          );
          return sharedSecret;
        } catch (error) {
          console.error("Error deriving shared secret:", error);
          return null;
        }
      },
      aes_gcm_decrypt: async (word: ArrayBuffer) => {
        const key = await get().getExchangeSecret();
        if (!key) throw new Error("Failed to get exchange secret");
        const aes_key = await window.crypto.subtle.importKey(
          "raw",
          key,
          "AES-GCM",
          false,
          ["decrypt"],
        );
        const combined = Buffer.from(word);
        const iv = Uint8Array.prototype.slice.call(combined, 0, 12);
        const cipher = Uint8Array.prototype.slice.call(combined, 12);
        const decrypted = await window.crypto.subtle.decrypt(
          {
            name: "AES-GCM",
            iv,
          },
          aes_key,
          cipher,
        );
        return decrypted;
      },
      decrypt: async (cipher: string) => {
        const decrypted = await get().aes_gcm_decrypt(
          Buffer.from(cipher, "base64").buffer,
        );
        return Buffer.from(decrypted).toString();
      },
      aes_gcm_encrypt: async (plain: ArrayBuffer) => {
        const key = await get().getExchangeSecret();
        if (!key) throw new Error("Failed to get exchange secret");
        const aes_key = await window.crypto.subtle.importKey(
          "raw",
          key,
          "AES-GCM",
          false,
          ["encrypt"],
        );
        const iv = new Uint8Array(12);
        window.crypto.getRandomValues(iv);
        const encrypted = await window.crypto.subtle.encrypt(
          {
            name: "AES-GCM",
            iv,
          },
          aes_key,
          plain,
        );
        return new Uint8Array([...iv, ...new Uint8Array(encrypted)]);
      },
      encrypt: async (word: string) => {
        const plain = Buffer.from(word);
        const encrypted = await get().aes_gcm_encrypt(plain.buffer);
        return Buffer.from(encrypted).toString("base64");
      },
    };
  },
);
